/**
 * Hook for transaction menu handlers (Edit, Duplicate, Delete, History)
 * Extracted from Activity.tsx to improve code organization
 */

import { useCallback } from "react";
import { ref, deleteObject } from "firebase/storage";
import { storage } from "../../../firebase";
import { Account, TransactionCategory, TransactionCategoryGroup, TransactionCategoryDetail, TransactionCustomFieldValues, TransactionCustomField, TransactionCustomFieldScopeType, CustomFieldAttachmentValue } from "../../../types";
import { TransactionWithDate } from "../../../utils/transactionProcessing";
import { mergeNoteAndAttachmentIntoCustomFieldValues } from "../../../utils/transactionCustomFieldsDerivation";
import { isDemoMode } from "../../../utils/demoMode";
import { formatTimeLocal, formatDateLocal, formatCurrency, copyToClipboard } from "../../../utils/transactionUtils";
import { api } from "../../../services/api";

export interface FormData {
  title: string;
  category: string;
  amount: string;
  currency: string;
  rate: string;
  defaultCurrencyRate: string;
  date: string;
  time: string;
  accountId: string;
  accountGroup: string;
  details?: Record<string, any>;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
  feeAmount?: string;
  feeAccountId?: string;
}

export interface TransferFormData {
  fromAccountId: string;
  toAccountId: string;
  amount: string;
  date: string;
  time: string;
  transferRate: string;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
  feeAmount?: string;
  feeAccountId?: string;
}

export interface UseTransactionMenuHandlersParams {
  selectedTransaction: TransactionWithDate | null;
  workspaceId: string;
  workspaceSlug: string | undefined;
  isMobile: boolean;
  navigate: (path: string, options?: { replace?: boolean; state?: any }) => void;
  accounts: Account[];
  defaultCurrency: string;
  expenseCategories: TransactionCategory[];
  incomeCategories: TransactionCategory[];
  categoryGroups: TransactionCategoryGroup[];
  transactions?: TransactionWithDate[]; // Optional transactions list to search for fee transactions
  getAccountById: (accountId: string | undefined) => Account | undefined;
  // State setters
  setEditingTransaction: (transaction: TransactionWithDate | null) => void;
  setDialogOpen: (open: boolean) => void;
  setTransferDialogOpen: (open: boolean) => void;
  setDialogType: (type: "expense" | "income") => void;
  setFormData: (data: FormData | ((prev: FormData) => FormData)) => void;
  setInitialFormData: (data: FormData | null) => void;
  setTransferFormData: (data: TransferFormData | ((prev: TransferFormData) => TransferFormData)) => void;
  setInitialTransferFormData: (data: TransferFormData | null) => void;
  setShowTime: (show: boolean) => void;
  setShowTransferTime: (show: boolean) => void;
  setShowTransferFee?: (show: boolean) => void;
  setSelectedCategoryDetails: (details: TransactionCategoryDetail[]) => void;
  setRateFromTransaction: (fromTransaction: boolean) => void;
  setRateManuallyEdited: (edited: boolean) => void;
  setDefaultCurrencyRateManuallyEdited: (edited: boolean) => void;
  calculateTransferRate: (fromCurrency: string, toCurrency: string) => number;
  setMenuAnchor: (anchor: HTMLElement | null) => void;
  setDeleteConfirmOpen: (open: boolean) => void;
  setSelectedTransaction: (transaction: TransactionWithDate | null) => void;
  setHistoryDialogOpen: (open: boolean) => void;
  setSnackbarElement: (element: { severity: string; message: string } | null) => void;
  setSnackbarOpen: (open: boolean) => void;
  transactionCustomFields?: TransactionCustomField[];
  // Functions
  handleMenuClose: () => void;
  fetchTransactionHistory: (transactionId: string, workspaceId: string) => Promise<void>;
}

export interface AccountBalanceChange {
  accountId: string;
  accountName: string;
  currentBalance: number;
  newBalance: number;
  currency: string;
}

export interface UseTransactionMenuHandlersResult {
  /** Open edit (or view) for selected transaction, or for the passed transaction when opening from shared URL. */
  handleEdit: (transactionOverride?: TransactionWithDate) => void;
  handleDuplicate: () => void;
  handleDelete: () => void;
  handleHistory: () => Promise<void>;
  handleCopyUrl: () => void;
  handleDeleteConfirm: () => Promise<void>;
  isProtectedAdjustment: (transaction: TransactionWithDate | null) => boolean;
  usesArchivedAccount: (transaction: TransactionWithDate) => boolean;
  getArchivedAccountNames: (transaction: TransactionWithDate) => string[];
  getAccountBalanceChanges: (transaction: TransactionWithDate | null) => AccountBalanceChange[];
}

/**
 * Hook for transaction menu handlers
 */
export const useTransactionMenuHandlers = (
  params: UseTransactionMenuHandlersParams
): UseTransactionMenuHandlersResult => {
  const {
    selectedTransaction,
    workspaceId,
    workspaceSlug,
    isMobile,
    navigate,
    accounts,
    defaultCurrency,
    expenseCategories,
    incomeCategories,
    categoryGroups,
    transactions,
    getAccountById,
    setEditingTransaction,
    setDialogOpen,
    setTransferDialogOpen,
    setDialogType,
    setFormData,
    setInitialFormData,
    setTransferFormData,
    setInitialTransferFormData,
    setShowTime,
    setShowTransferTime,
    setShowTransferFee,
    setSelectedCategoryDetails,
    setRateFromTransaction,
    setRateManuallyEdited,
    setDefaultCurrencyRateManuallyEdited,
    calculateTransferRate,
    setMenuAnchor,
    setDeleteConfirmOpen,
    setSelectedTransaction: setSelectedTransactionState,
    setHistoryDialogOpen,
    setSnackbarElement,
    setSnackbarOpen,
    transactionCustomFields = [],
    handleMenuClose,
    fetchTransactionHistory,
  } = params;

  // Check if transaction is a protected adjustment (cannot be edited/deleted/duplicated)
  const isProtectedAdjustment = useCallback((transaction: TransactionWithDate | null): boolean => {
    return transaction?.type === "adjustment" || false;
  }, []);

  // Check if transaction uses an archived account
  const usesArchivedAccount = useCallback((transaction: TransactionWithDate): boolean => {
    // Check accountId (for expense/income/adjustment)
    if (transaction.accountId) {
      const account = getAccountById(transaction.accountId);
      if (account?.isArchived) return true;
    }
    
    // Check fromAccountId (for transfers)
    if (transaction.fromAccountId) {
      const fromAccount = getAccountById(transaction.fromAccountId);
      if (fromAccount?.isArchived) return true;
    }
    
    // Check toAccountId (for transfers)
    if (transaction.toAccountId) {
      const toAccount = getAccountById(transaction.toAccountId);
      if (toAccount?.isArchived) return true;
    }
    
    return false;
  }, [getAccountById]);

  // Get archived account names for message
  const getArchivedAccountNames = useCallback((transaction: TransactionWithDate): string[] => {
    const archivedNames: string[] = [];
    
    if (transaction.accountId) {
      const account = getAccountById(transaction.accountId);
      if (account?.isArchived && account.accountName) {
        archivedNames.push(account.accountName);
      }
    }
    
    if (transaction.fromAccountId) {
      const fromAccount = getAccountById(transaction.fromAccountId);
      if (fromAccount?.isArchived && fromAccount.accountName && !archivedNames.includes(fromAccount.accountName)) {
        archivedNames.push(fromAccount.accountName);
      }
    }
    
    if (transaction.toAccountId) {
      const toAccount = getAccountById(transaction.toAccountId);
      if (toAccount?.isArchived && toAccount.accountName && !archivedNames.includes(toAccount.accountName)) {
        archivedNames.push(toAccount.accountName);
      }
    }
    
    return archivedNames;
  }, [getAccountById]);

  // Handle edit (optional transactionOverride when opening from shared URL view)
  const handleEdit = useCallback((transactionOverride?: TransactionWithDate): void => {
    const tx = transactionOverride ?? selectedTransaction;
    if (!tx || isProtectedAdjustment(tx) || usesArchivedAccount(tx)) return;

    // On mobile, navigate to edit page instead of opening dialog (pass transaction so edit opens instantly)
    if (isMobile && workspaceSlug) {
      handleMenuClose();
      const transactionType = tx.type === "transfer"
        ? "transfer"
        : tx.isIncome
          ? "income"
          : "expense";
      navigate(`/${workspaceSlug}/edit-transaction/${transactionType}/${tx.id}`, {
        state: { transaction: tx },
      });
      return;
    }
    
    // Desktop: use dialog (existing behavior)
    // Handle transfer transactions
    if (tx.type === "transfer") {
      setEditingTransaction(tx);
      // Use the date directly - it's already a Date object from transaction loading
      const transactionDate = tx.date || new Date();
      const transactionTime = formatTimeLocal(transactionDate);
      // Only show time field if transaction has a time (not 00:00, which indicates no time was set)
      const hasTime = transactionTime !== "00:00";
      
      // Find fee transaction linked to this transfer (check in loaded transactions first)
      let feeAmount: string | undefined;
      let feeAccountId: string | undefined;
      
      if (transactions && transactions.length > 0) {
        // Search in already loaded transactions
        const feeTransaction = transactions.find((t) => {
          const hasTransferFeeFor = (t as any).transferFeeFor === tx.id;
          const isTransferFee = t.title === "Transfer Fee" || t.category === "Transfer Fee";
          const isExpense = t.type === "expense";
          return hasTransferFeeFor && isTransferFee && isExpense;
        });
        
        if (feeTransaction) {
          feeAmount = feeTransaction.amount?.toString();
          feeAccountId = feeTransaction.accountId;
          console.log("Found fee transaction in loaded transactions:", { feeAmount, feeAccountId, transferId: tx.id });
        }
      }
      
      // If not found in loaded transactions, try fetching
      const loadFeeData = async () => {
        if (feeAmount !== undefined && feeAccountId) return; // Already found
        
        try {
          const response = await api.makeAuthenticatedRequest(
            `/workspaces/${workspaceId}/transactions?type=expense`,
            { method: "GET" }
          );
          
          if (response.ok) {
            const fetchedTransactions = await response.json();
            // Find fee transaction linked to this transfer
            const feeTransaction = fetchedTransactions.find((t: any) => {
              const hasTransferFeeFor = t.transferFeeFor === tx.id;
              const isTransferFee = t.title === "Transfer Fee" || t.category === "Transfer Fee";
              return hasTransferFeeFor && isTransferFee;
            });
            
            if (feeTransaction) {
              const foundFeeAmount = feeTransaction.amount?.toString();
              const foundFeeAccountId = feeTransaction.accountId;
              
              console.log("Found fee transaction via API:", { foundFeeAmount, foundFeeAccountId, transferId: tx.id });
              
              // Update form data with fee information
              setTransferFormData((prev) => ({
                ...prev,
                feeAmount: foundFeeAmount,
                feeAccountId: foundFeeAccountId,
              }));
              
              // Show fee fields
              if (setShowTransferFee) {
                setShowTransferFee(true);
              }
            } else {
              console.log("No fee transaction found for transfer:", tx.id);
            }
          }
        } catch (error) {
          console.error("Error loading fee data:", error);
          // Continue without fee data if there's an error
        }
      };
      
      const initialTransferData: TransferFormData = {
        fromAccountId: tx.fromAccountId || "",
        toAccountId: tx.toAccountId || "",
        amount: tx.amount?.toString() || "",
        date: formatDateLocal(transactionDate),
        time: hasTime ? transactionTime : "",
        transferRate: tx.transferRate?.toString() || "",
        customFieldValues: mergeNoteAndAttachmentIntoCustomFieldValues(
          transactionCustomFields,
          "transfer",
          tx.customFieldValues,
          tx.note,
          tx.attachmentUrl ?? null,
          tx.attachmentName ?? null,
          tx.attachmentType ?? null
        ),
        customFieldPendingFiles: undefined,
        feeAmount: feeAmount,
        feeAccountId: feeAccountId,
      };
      setTransferFormData(initialTransferData);
      setInitialTransferFormData(initialTransferData);
      // Only show time field if transaction has a time
      setShowTransferTime(hasTime);
      
      // Show fee fields if fee data was found synchronously
      if (feeAmount !== undefined && feeAccountId && setShowTransferFee) {
        console.log("Showing fee fields for transfer:", tx.id);
        setShowTransferFee(true);
      }
      
      // Load fee data asynchronously if not found in loaded transactions
      if (feeAmount === undefined || !feeAccountId) {
        loadFeeData();
      }
      
      handleMenuClose();
      setTransferDialogOpen(true);
      return;
    }
    
    // Check if this is a fee transaction (transfer fee or expense fee)
    const isTransferFee = tx.type === "expense" &&
      (tx.title === "Transfer Fee" || tx.category === "Transfer Fee") &&
      tx.transferFeeFor;
    const isExpenseFee = tx.type === "expense" &&
      (tx.title === "Expense Fee" || tx.category === "Expense Fee") &&
      (tx as any).expenseFeeFor;
    const isFeeTransaction = isTransferFee || isExpenseFee;

    if (isFeeTransaction) {
      // Handle fee transaction - open fee edit dialog instead
      // This will be handled by the parent component via a callback
      // For now, we'll set a flag that the parent can check
      // The parent should check for feeTransaction and open FeeEditDialog
      setEditingTransaction(tx);
      handleMenuClose();
      // Note: Parent component should check if editingTransaction is a fee transaction
      // and open FeeEditDialog instead of regular dialog
      return;
    }
    
    // Handle income/expense transactions
    setEditingTransaction(tx);
    setDialogType(tx.isIncome ? "income" : "expense");
    
    // Get account from transaction
    const accountId = tx.accountId || "";
    const account = accountId ? getAccountById(accountId) : undefined;
    
    const accountCurrency = account?.currency || defaultCurrency;
    const transactionCurrency = tx.currency || accountCurrency;
    const existingRate = tx.rate?.toString() || "";
    // Use stored defaultCurrencyRate if available, otherwise calculate from amountInDefaultCurrency
    // DO NOT auto-fill with current setting value - only use saved value or calculated from saved amountInDefaultCurrency
    let existingDefaultCurrencyRate = "";

    if (tx.defaultCurrencyRate !== undefined && tx.defaultCurrencyRate !== null) {
      const rawRate = tx.defaultCurrencyRate;
      const rateValue = typeof rawRate === "number" ? rawRate : parseFloat(String(rawRate));
      if (!isNaN(rateValue) && rateValue > 0) {
        // Stored rate 1 usually means default currency matched transaction currency at save time.
        // If default has since changed, treat as stale and use current settings rate (leave empty so fallback runs).
        const isStaleSameCurrencyRate =
          transactionCurrency !== defaultCurrency && Math.abs(rateValue - 1) < 1e-6;
        if (!isStaleSameCurrencyRate) {
          // Stored rate is expense-to-default, but field needs account-to-default
          // Convert: expense-to-default -> account-to-default
          // expenseAmount * expenseToDefaultRate = defaultAmount
          // accountAmount * accountToDefaultRate = defaultAmount
          // So: expenseAmount * expenseToDefaultRate = accountAmount * accountToDefaultRate
          // And: accountAmount = expenseAmount * exchangeRate (expense-to-account)
          // So: expenseAmount * expenseToDefaultRate = expenseAmount * exchangeRate * accountToDefaultRate
          // Therefore: accountToDefaultRate = expenseToDefaultRate / exchangeRate
          const exchangeRate = tx.rate || 1;
          const accountToDefaultRate = exchangeRate > 0 ? rateValue / exchangeRate : rateValue;
          const rounded = Math.round(accountToDefaultRate * 100000) / 100000;
          existingDefaultCurrencyRate = rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(5).replace(/\.?0+$/, "");
        }
      }
    }
    // Only derive rate from amountInDefaultCurrency when default currency hasn't changed; otherwise it's in old default and would yield wrong rate (e.g. 1).
    if (!existingDefaultCurrencyRate && tx.defaultCurrencyAtTime && tx.defaultCurrencyAtTime === defaultCurrency && tx.amountInDefaultCurrency && tx.amount) {
      if (transactionCurrency !== defaultCurrency && accountCurrency !== defaultCurrency) {
        // Calculate account-to-default rate from amountInDefaultCurrency
        // expenseAmount * exchangeRate = accountAmount
        // accountAmount * accountToDefaultRate = defaultAmount
        // So: accountToDefaultRate = defaultAmount / accountAmount = defaultAmount / (expenseAmount * exchangeRate)
        const exchangeRate = tx.rate || 1;
        const accountAmount = tx.amount * exchangeRate;
        const accountToDefaultRate = accountAmount > 0 ? tx.amountInDefaultCurrency / accountAmount : 0;
        if (accountToDefaultRate > 0) {
          const rounded = Math.round(accountToDefaultRate * 100000) / 100000;
          existingDefaultCurrencyRate = rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(5).replace(/\.?0+$/, "");
        }
      }
    }

    // If still empty and account currency differs from default, use current setting rate so the field isn't blank
    if (!existingDefaultCurrencyRate && accountCurrency !== defaultCurrency) {
      const calculatedAccountToDefaultRate = calculateTransferRate(accountCurrency, defaultCurrency);
      const rounded = Math.round(calculatedAccountToDefaultRate * 100000) / 100000;
      existingDefaultCurrencyRate = rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(5).replace(/\.?0+$/, "");
    }
    // Note: We prefer saved value or amountInDefaultCurrency; only use setting rate when neither exists (e.g. old transactions).

    // Track if rate came from existing transaction
    setRateFromTransaction(!!existingRate);
    
    // If rate exists and differs from calculated rate, assume it was manually edited
    // This preserves manually changed rates when editing
    if (existingRate && transactionCurrency !== accountCurrency) {
      const calculatedRate = calculateTransferRate(transactionCurrency, accountCurrency);
      const storedRate = parseFloat(existingRate);
      if (!isNaN(storedRate) && storedRate > 0) {
        const rateDifference = Math.abs(storedRate - calculatedRate) / Math.max(storedRate, calculatedRate);
        // If rate differs by more than 0.1%, assume it was manually edited
        if (rateDifference > 0.001) {
          setRateManuallyEdited(true);
        } else {
          setRateManuallyEdited(false);
        }
      } else {
        setRateManuallyEdited(false);
      }
    } else {
      setRateManuallyEdited(false);
    }
    
    // Same logic for default currency rate
    // If default currency rate doesn't exist in transaction, it will be auto-filled by useRateAutoUpdate
    if (existingDefaultCurrencyRate && transactionCurrency !== defaultCurrency) {
      const calculatedDefaultRate = calculateTransferRate(transactionCurrency, defaultCurrency);
      const storedDefaultRate = parseFloat(existingDefaultCurrencyRate);
      if (!isNaN(storedDefaultRate) && storedDefaultRate > 0) {
        const defaultRateDifference = Math.abs(storedDefaultRate - calculatedDefaultRate) / Math.max(storedDefaultRate, calculatedDefaultRate);
        if (defaultRateDifference > 0.001) {
          setDefaultCurrencyRateManuallyEdited(true);
        } else {
          setDefaultCurrencyRateManuallyEdited(false);
        }
      } else {
        setDefaultCurrencyRateManuallyEdited(false);
      }
    } else {
      // No existing default currency rate - allow auto-fill
      setDefaultCurrencyRateManuallyEdited(false);
    }
    
    // Load category details if category exists (details are now at group level)
    const categoryName = tx.category || "";
    const categories = tx.isIncome ? incomeCategories : expenseCategories;
    const selectedCategory = categories.find((cat) => cat.name === categoryName);
    // Get details from the category's group
    const group = selectedCategory?.groupId ? categoryGroups.find((g) => g.id === selectedCategory.groupId) : null;
    const allGroupDetails = group?.details || [];
    
    // IMPORTANT: Only show details that are explicitly enabled (checked) for this category
    // If enabledDetails is empty or undefined, show NO details
    const enabledDetailNames = selectedCategory?.enabledDetails && Array.isArray(selectedCategory.enabledDetails)
      ? selectedCategory.enabledDetails 
      : [];
    
    // Only filter if enabledDetails exists and is not empty
    const details = enabledDetailNames.length > 0 
      ? allGroupDetails.filter(detail => enabledDetailNames.includes(detail.name))
      : []; // If no enabled details, show nothing
    
    // Set selected category details for the form (only enabled ones)
    setSelectedCategoryDetails(details);
    
    // Use the date directly - it's already a Date object from transaction loading
    const transactionDate = tx.date || new Date();
    const transactionTime = formatTimeLocal(transactionDate);
    // Only show time field if transaction has a time (not 00:00, which indicates no time was set)
    const hasTime = transactionTime !== "00:00";
    const scope: TransactionCustomFieldScopeType = tx.isIncome ? "income" : "expense";
    // When editing an expense, find linked fee (expenseFeeFor) to prefill fee fields
    let feeAmount: string | undefined;
    let feeAccountId: string | undefined;
    if (!tx.isIncome && transactions && transactions.length > 0) {
      const feeTx = transactions.find((t) => (t as any).expenseFeeFor === tx.id && (t as any).type === "expense");
      if (feeTx) {
        feeAmount = feeTx.amount?.toString();
        feeAccountId = feeTx.accountId;
      }
    }
    const initialData: FormData = {
      title: tx.title || "",
      category: categoryName,
      amount: tx.amount?.toString() || "",
      currency: transactionCurrency,
      rate: existingRate,
      defaultCurrencyRate: existingDefaultCurrencyRate,
      date: formatDateLocal(transactionDate),
      time: hasTime ? transactionTime : "",
      accountId: accountId,
      accountGroup: account ? account.group : "",
      details: tx.details,
      customFieldValues: mergeNoteAndAttachmentIntoCustomFieldValues(
        transactionCustomFields,
        scope,
        tx.customFieldValues,
        tx.note,
        tx.attachmentUrl ?? null,
        tx.attachmentName ?? null,
        tx.attachmentType ?? null
      ),
      customFieldPendingFiles: undefined,
      ...(feeAmount !== undefined && feeAccountId !== undefined ? { feeAmount, feeAccountId } : {}),
    };
    setFormData(initialData);
    setInitialFormData(initialData);
    // Only show time field if transaction has a time
    setShowTime(hasTime);
    
    handleMenuClose();
    setDialogOpen(true);
  }, [
    selectedTransaction,
    isProtectedAdjustment,
    usesArchivedAccount,
    isMobile,
    workspaceSlug,
    navigate,
    handleMenuClose,
    setEditingTransaction,
    setTransferFormData,
    setInitialTransferFormData,
    setShowTransferTime,
    setTransferDialogOpen,
    setDialogType,
    getAccountById,
    defaultCurrency,
    setRateFromTransaction,
    setRateManuallyEdited,
    incomeCategories,
    expenseCategories,
    categoryGroups,
    setSelectedCategoryDetails,
    setFormData,
    setInitialFormData,
    setShowTime,
    setDialogOpen,
    transactionCustomFields,
  ]);

  // Handle duplicate
  const handleDuplicate = useCallback((): void => {
    if (!selectedTransaction || isProtectedAdjustment(selectedTransaction) || usesArchivedAccount(selectedTransaction)) return;

    if (isMobile && workspaceSlug) {
      handleMenuClose();
      const transactionType =
        selectedTransaction.type === "transfer"
          ? "transfer"
          : selectedTransaction.isIncome
            ? "income"
            : "expense";
      navigate(`/${workspaceSlug}/create-transaction/${transactionType}`, {
        state: { duplicateTransactionId: selectedTransaction.id },
      });
      return;
    }
    
    // Handle transfer transactions (desktop: open dialog)
    if (selectedTransaction.type === "transfer") {
      setEditingTransaction(null); // Not editing, creating a duplicate
      setTransferFormData({
        fromAccountId: selectedTransaction.fromAccountId || "",
        toAccountId: selectedTransaction.toAccountId || "",
        amount: selectedTransaction.amount?.toString() || "",
        date: formatDateLocal(new Date()), // Use today's date in local timezone for duplicate
        time: "",
        transferRate: selectedTransaction.transferRate?.toString() || "",
        customFieldValues: mergeNoteAndAttachmentIntoCustomFieldValues(
          transactionCustomFields,
          "transfer",
          selectedTransaction.customFieldValues,
          selectedTransaction.note,
          selectedTransaction.attachmentUrl ?? null,
          selectedTransaction.attachmentName ?? null,
          selectedTransaction.attachmentType ?? null
        ),
        customFieldPendingFiles: undefined,
      });
      handleMenuClose();
      setTransferDialogOpen(true);
      return;
    }
    
    // Handle income/expense transactions
    // Get account from transaction (same logic as handleEdit)
    const accountId = selectedTransaction.accountId || "";
    const account = accountId ? getAccountById(accountId) : undefined;
    
    const dupScope: TransactionCustomFieldScopeType = selectedTransaction.isIncome ? "income" : "expense";
    const duplicateData: FormData = {
      title: `${selectedTransaction.title || ""} (Copy)`,
      category: selectedTransaction.category || "",
      amount: selectedTransaction.amount?.toString() || "",
      currency: selectedTransaction.currency || "",
      rate: selectedTransaction.rate?.toString() || "",
      defaultCurrencyRate: "", // Will be auto-calculated
      date: formatDateLocal(new Date()), // Use today's date in local timezone for duplicate
      time: "",
      accountId: accountId,
      accountGroup: account ? account.group : "",
      details: selectedTransaction.details, // Preserve details from original transaction
      customFieldValues: mergeNoteAndAttachmentIntoCustomFieldValues(
        transactionCustomFields,
        dupScope,
        selectedTransaction.customFieldValues,
        selectedTransaction.note,
        selectedTransaction.attachmentUrl ?? null,
        selectedTransaction.attachmentName ?? null,
        selectedTransaction.attachmentType ?? null
      ),
      customFieldPendingFiles: undefined,
    };
    setEditingTransaction(null);
    setDialogType(selectedTransaction.isIncome ? "income" : "expense");
    
    // Load category details if category exists (same logic as handleEdit)
    const categoryName = selectedTransaction.category || "";
    const categories = selectedTransaction.isIncome ? incomeCategories : expenseCategories;
    const selectedCategory = categories.find((cat) => cat.name === categoryName);
    // Get details from the category's group
    const group = selectedCategory?.groupId ? categoryGroups.find((g) => g.id === selectedCategory.groupId) : null;
    const allGroupDetails = group?.details || [];
    
    // IMPORTANT: Only show details that are explicitly enabled (checked) for this category
    const enabledDetailNames = selectedCategory?.enabledDetails && Array.isArray(selectedCategory.enabledDetails)
      ? selectedCategory.enabledDetails 
      : [];
    
    // Only filter if enabledDetails exists and is not empty
    const details = enabledDetailNames.length > 0 
      ? allGroupDetails.filter(detail => enabledDetailNames.includes(detail.name))
      : [];
    
    // Set selected category details for the form
    setSelectedCategoryDetails(details);
    
    setFormData(duplicateData);
    setInitialFormData(duplicateData);
    handleMenuClose();
    setDialogOpen(true);
  }, [
    selectedTransaction,
    isProtectedAdjustment,
    usesArchivedAccount,
    isMobile,
    workspaceSlug,
    navigate,
    handleMenuClose,
    getAccountById,
    incomeCategories,
    expenseCategories,
    categoryGroups,
    setSelectedCategoryDetails,
    setEditingTransaction,
    setTransferFormData,
    setTransferDialogOpen,
    setDialogType,
    setFormData,
    setInitialFormData,
    setDialogOpen,
    transactionCustomFields,
  ]);

  // Handle delete button click
  /**
   * Handle delete button click
   * 
   * DEMO MODE: Blocked - demo mode is read-only
   * This function checks demo mode before allowing deletion.
   */
  const handleDelete = useCallback((): void => {
    if (!selectedTransaction || isProtectedAdjustment(selectedTransaction)) return;
    
    // DEMO MODE: Block all delete operations - demo mode is read-only
    if (isDemoMode()) {
      setSnackbarElement({
        severity: "info",
        message: "Demo mode: Transactions cannot be deleted. Please log in to delete transactions.",
      });
      setSnackbarOpen(true);
      setMenuAnchor(null);
      return;
    }
    
    setMenuAnchor(null); // Close menu but keep selectedTransaction
    setDeleteConfirmOpen(true);
  }, [
    selectedTransaction,
    isProtectedAdjustment,
    setSnackbarElement,
    setSnackbarOpen,
    setMenuAnchor,
    setDeleteConfirmOpen,
  ]);

  const handleCopyUrl = useCallback((): void => {
    if (!selectedTransaction || !workspaceSlug) return;
    const url = `${window.location.origin}/${workspaceSlug}/transaction/${selectedTransaction.id}`;
    copyToClipboard(url).then((ok) => {
      setSnackbarElement({
        severity: ok ? "success" : "error",
        message: ok ? "Link copied" : "Failed to copy link",
      });
      setSnackbarOpen(true);
    });
    setMenuAnchor(null);
  }, [selectedTransaction, workspaceSlug, setSnackbarElement, setSnackbarOpen, setMenuAnchor]);

  /**
   * Calculate account balance changes that will occur when deleting a transaction
   */
  const getAccountBalanceChanges = useCallback((transaction: TransactionWithDate | null): AccountBalanceChange[] => {
    if (!transaction) return [];

    const changes: AccountBalanceChange[] = [];

    // Determine transaction type - handle both explicit type and legacy isIncome field
    const transactionType = transaction.type || (transaction.isIncome ? "income" : "expense");

    if (transactionType === "transfer") {
      // For transfers, both accounts are affected
      const fromAccountId = transaction.fromAccountId;
      const toAccountId = transaction.toAccountId;
      const amount = transaction.amount || 0;
      const transferRate = transaction.transferRate;

      if (fromAccountId) {
        const fromAccount = getAccountById(fromAccountId);
        if (fromAccount) {
          changes.push({
            accountId: fromAccount.id,
            accountName: fromAccount.accountName,
            currentBalance: fromAccount.amount || 0,
            newBalance: (fromAccount.amount || 0) + amount, // Add back to from account
            currency: fromAccount.currency,
          });
        } else if (process.env.NODE_ENV === "development") {
          console.warn("Account not found for transfer fromAccountId:", fromAccountId);
        }
      }

      if (toAccountId) {
        const toAccount = getAccountById(toAccountId);
        if (toAccount) {
          // Match backend logic: transferRate ? amount * transferRate : amount
          const toAmount = transferRate ? amount * transferRate : amount;
          changes.push({
            accountId: toAccount.id,
            accountName: toAccount.accountName,
            currentBalance: toAccount.amount || 0,
            newBalance: (toAccount.amount || 0) - toAmount, // Subtract from to account
            currency: toAccount.currency,
          });
        } else if (process.env.NODE_ENV === "development") {
          console.warn("Account not found for transfer toAccountId:", toAccountId);
        }
      }
    } else if (transactionType === "expense" || transactionType === "income") {
      // For expense/income, one account is affected
      const accountId = transaction.accountId;
      if (accountId) {
        const account = getAccountById(accountId);
        if (account) {
          const amount = transaction.amount || 0;
          let newBalance: number;
          // Use isIncome if available, otherwise use type
          const isIncome = transaction.isIncome !== undefined ? transaction.isIncome : transactionType === "income";
          if (isIncome) {
            // For income: subtract the amount (reversing the income)
            newBalance = (account.amount || 0) - amount;
          } else {
            // For expense: add back the amount (reversing the expense)
            newBalance = (account.amount || 0) + amount;
          }
          changes.push({
            accountId: account.id,
            accountName: account.accountName,
            currentBalance: account.amount || 0,
            newBalance,
            currency: account.currency,
          });
        } else if (process.env.NODE_ENV === "development") {
          console.warn("Account not found for transaction accountId:", accountId, "Transaction:", transaction.id);
        }
      }
    }
    // Note: adjustment transactions are protected and cannot be deleted, so we don't handle them here

    return changes;
  }, [getAccountById]);

  /**
   * Confirm and execute transaction deletion
   * 
   * DEMO MODE: Blocked - demo mode is read-only
   * This function performs API calls (deleteTransaction) and storage operations (deleteObject).
   * All operations are blocked in demo mode.
   */
  const handleDeleteConfirm = useCallback(async (): Promise<void> => {
    if (!selectedTransaction || !workspaceId) return;

    // DEMO MODE: Block all delete operations - demo mode is read-only
    // This prevents:
    // - Database writes (transaction deletion, account balance updates)
    // - Storage operations (attachment deletion)
    if (isDemoMode()) {
      setSnackbarElement({
        severity: "info",
        message: "Demo mode: Transactions cannot be deleted. Please log in to delete transactions.",
      });
      setSnackbarOpen(true);
      setDeleteConfirmOpen(false);
      setSelectedTransactionState(null);
      return;
    }

    try {
      // Delete attachment from storage if exists
      if (selectedTransaction.attachmentUrl) {
        try {
          const attachmentRef = ref(storage, selectedTransaction.attachmentUrl);
          await deleteObject(attachmentRef);
        } catch (error) {
          console.log("Error deleting attachment:", error);
          // Continue with transaction deletion even if attachment deletion fails
        }
      }

      // Store transaction ID before clearing state
      const transactionId = selectedTransaction.id;
      
      // Clear state FIRST to immediately close dialog
      setDeleteConfirmOpen(false);
      setSelectedTransactionState(null);
      
      // Use API to delete transaction (handles account balance updates)
      await api.deleteTransaction(workspaceId, transactionId);
      
      // Show success message
      setSnackbarElement({
        severity: "success",
        message: "Transaction deleted successfully",
      });
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error deleting transaction:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to delete transaction";
      setSnackbarElement({
        severity: "error",
        message: errorMessage,
      });
      setSnackbarOpen(true);
      // Don't close dialog on error so user can try again
    }
  }, [
    selectedTransaction,
    workspaceId,
    setSnackbarElement,
    setSnackbarOpen,
    setDeleteConfirmOpen,
    setSelectedTransactionState,
  ]);

  // Handle history button click
  const handleHistory = useCallback(async (): Promise<void> => {
    if (!selectedTransaction || !workspaceId) return;
    handleMenuClose();
    await fetchTransactionHistory(selectedTransaction.id, workspaceId);
    setHistoryDialogOpen(true);
  }, [selectedTransaction, workspaceId, handleMenuClose, fetchTransactionHistory, setHistoryDialogOpen]);

  return {
    handleEdit,
    handleDuplicate,
    handleDelete,
    handleHistory,
    handleCopyUrl,
    handleDeleteConfirm,
    isProtectedAdjustment,
    usesArchivedAccount,
    getArchivedAccountNames,
    getAccountBalanceChanges,
  };
};
